import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  IDashboardFolderRenameMutation,
  IDashboardFolderRenameMutationVariables,
  ISubFolderFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertCondition } from '../../../../utils/types';
import {
  dashboardInstanceUpdatePath,
  renameSubFolder,
} from '../../../../redux/slices/dashboards/dashboardInstance';
import { dashboardFolderRenameMutation } from '../../../../utils/apollo/dashboards';
import { useAppDispatch } from '../../../../redux/hooks';
import { DashboardNavItem } from '../components';
import SubFolderActions from './SubFolderActions';
import EditableDashboardFolder from './EditableDashboardFolder';
import toastMessage from '../../../../utils/toastMessage';

interface IProps {
  subFolders: ISubFolderFieldsFragment[];
  onChangeFolder: (folderId: string) => void;
  mouseOverId: string | null;
  setMouseOverId: (mouseOverId: string | null) => void;
  openedMenuId: string | null;
  setOpenedMenuId: (mouseOverId: string | null) => void;
}

const PublishedDashboardSubFolders = ({
  subFolders,
  onChangeFolder,
  mouseOverId,
  setMouseOverId,
  openedMenuId,
  setOpenedMenuId,
}: IProps) => {
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const { dashboardBaseId } = urlParams;

  const handleRenamedFolder = ({
    dashboardFolderRename,
  }: IDashboardFolderRenameMutation) => {
    const { entity, errors } = dashboardFolderRename;

    setRenamingFolderId(null);

    if (printGraphqlErrors(errors)) {
      return;
    }
    assertCondition(entity && entity.id !== null);

    dispatch(
      renameSubFolder({
        subFolderId: entity.id,
        newName: entity.name,
      }),
    );

    if (
      dashboardBaseId &&
      entity.templates.some((template) => template.baseId === dashboardBaseId)
    ) {
      dispatch(dashboardInstanceUpdatePath({ newPath: entity.path }));
    }

    toastMessage({
      message: `Folder renamed to ${entity.name}`,
      type: 'info',
    });
  };

  const [dashboardFolderRename] = useMutation<
    IDashboardFolderRenameMutation,
    IDashboardFolderRenameMutationVariables
  >(dashboardFolderRenameMutation, {
    onCompleted: handleRenamedFolder,
  });

  const handleRename =
    (subFolder: ISubFolderFieldsFragment) => (newName: string) => {
      if (subFolder.name === newName) {
        setRenamingFolderId(null);
      } else {
        dashboardFolderRename({
          variables: {
            input: {
              id: subFolder.id,
              name: newName,
            },
          },
        });
      }
    };

  return (
    <>
      {subFolders.map((subFolder) => {
        const id = `folder-${subFolder.id}`;
        const isFocused = mouseOverId === id || openedMenuId === id;
        const isRenaming = renamingFolderId === id;

        if (isRenaming) {
          return (
            <EditableDashboardFolder
              key={id}
              value={subFolder.name}
              onSubmit={handleRename(subFolder)}
              onCancel={() => setRenamingFolderId(null)}
              allowSameValue={true}
            />
          );
        }

        return (
          <DashboardNavItem
            key={id}
            iconName="folder"
            title={subFolder.name}
            onClick={() => onChangeFolder(subFolder.id)}
            onMouseEnter={() => {
              setMouseOverId(id);
            }}
            onMouseLeave={() => {
              setMouseOverId(null);
            }}
            isFocused={mouseOverId === id}
          >
            {isFocused && (
              <SubFolderActions
                subFolder={subFolder}
                onOpen={() => {
                  setOpenedMenuId(id);
                }}
                onClose={() => {
                  setOpenedMenuId(null);
                }}
                triggerRenameSubFolder={() => setRenamingFolderId(id)}
              />
            )}
          </DashboardNavItem>
        );
      })}
    </>
  );
};

export default PublishedDashboardSubFolders;
